import * as React from "react";

import { Button, type ButtonProps } from "./button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

export interface IconTooltipButtonProps extends ButtonProps {
  /** Visible tooltip text (also used as `aria-label` when that is omitted). */
  tooltip: React.ReactNode;
  side?: React.ComponentPropsWithoutRef<typeof TooltipContent>["side"];
}

/**
 * Icon-only button with an accessible tooltip.
 * Use for table action columns and other chrome where the label is not visible.
 */
const IconTooltipButton = React.forwardRef<HTMLButtonElement, IconTooltipButtonProps>(
  (
    { tooltip, side = "top", children, disabled, loading, "aria-label": ariaLabel, ...props },
    ref,
  ) => {
    const label =
      ariaLabel ??
      (typeof tooltip === "string" || typeof tooltip === "number" ? String(tooltip) : undefined);

    const isDisabled = Boolean(disabled || loading);

    const button = (
      <Button
        ref={ref}
        type="button"
        disabled={disabled}
        loading={loading}
        aria-label={label}
        {...props}
      >
        {children}
      </Button>
    );

    // Disabled/loading buttons do not fire pointer events — wrap so the tooltip still works.
    const trigger = isDisabled ? <span className="inline-flex">{button}</span> : button;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side={side}>{tooltip}</TooltipContent>
      </Tooltip>
    );
  },
);
IconTooltipButton.displayName = "IconTooltipButton";

export { IconTooltipButton };
